import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ModalComponentProps } from '@components/modals/constants';
import { Button } from '@components/primitives/Button/Button';
import type { AppointmentResponse } from '@features/appointments';
import {
  clearWaitingRoomLaunchIntent,
  selectWaitingRoomLaunchIntent,
} from '@features/telehealth/store';
import type { TelehealthEncounterRouteState } from '@features/telehealth/types';
import {
  useRequireScheduledAppointmentForNoteCreation,
  useSubjectLanguageVariants,
} from '@features/user';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { AppointmentsList } from './AppointmentsList';

interface AppointmentRecommendation {
  type: string;
  appointment: AppointmentResponse;
  confidence: string;
  message: string;
}

interface EligibleAppointmentContext {
  appointment: AppointmentResponse;
  timing: {
    minutes_until_start?: number;
    minutes_remaining?: number;
    minutes_elapsed?: number;
    is_overtime?: boolean;
    is_within_grace_period?: boolean;
  };
  overtimeMinutes?: number;
}

/**
 * Modal for selecting which appointment to associate with a recording session.
 * Shows current and upcoming appointments with timing information and recommendations.
 * Receives pre-filtered eligible appointments from useAppointmentCollision hook.
 */
const AppointmentSelectionModal: React.FC<ModalComponentProps> = ({ modalProps, closeModal }) => {
  // Extract typed properties from modalProps
  const { eligibleCurrent, eligibleOvertime, eligibleNext, recommendation, telehealthLaunch } =
    modalProps as {
      eligibleCurrent?: EligibleAppointmentContext[] | null;
      eligibleOvertime?: EligibleAppointmentContext[] | null;
      eligibleNext?: EligibleAppointmentContext[] | null;
      recommendation?: AppointmentRecommendation | null;
      telehealthLaunch?: boolean;
    };
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { appointment } = useEventTracking();
  const { singularCap } = useSubjectLanguageVariants();
  const requiresScheduledAppointmentForNoteCreation =
    useRequireScheduledAppointmentForNoteCreation();
  const waitingRoomLaunchIntent = useAppSelector(selectWaitingRoomLaunchIntent);
  const isWaitingRoomTelehealthLaunch = Boolean(telehealthLaunch && waitingRoomLaunchIntent);

  const telehealthRouteState = useMemo<TelehealthEncounterRouteState | undefined>(
    () =>
      isWaitingRoomTelehealthLaunch
        ? {
            telehealthLaunch: {
              source: 'waiting-room',
            },
          }
        : undefined,
    [isWaitingRoomTelehealthLaunch],
  );

  const handleClose = () => {
    if (isWaitingRoomTelehealthLaunch) {
      dispatch(clearWaitingRoomLaunchIntent());
    }
    closeModal();
  };

  // Build array of appointments for the list from all eligible sources
  // These arrays are already filtered by useAppointmentCollision hook
  const appointments: AppointmentResponse[] = [];

  // Add all eligible current appointments
  if (eligibleCurrent && Array.isArray(eligibleCurrent)) {
    appointments.push(...eligibleCurrent.map(ctx => ctx.appointment));
  }

  // Add all eligible overtime appointments
  if (eligibleOvertime && Array.isArray(eligibleOvertime)) {
    appointments.push(...eligibleOvertime.map(ctx => ctx.appointment));
  }

  // Add all eligible next appointments
  if (eligibleNext && Array.isArray(eligibleNext)) {
    appointments.push(...eligibleNext.map(ctx => ctx.appointment));
  }

  // GDN-2784: candidates spanning more than one calendar day must be grouped
  // under date headers — two cards for the same weekly slot on different days
  // were visually identical.
  const spansMultipleDays =
    new Set(
      appointments
        .map(appointment =>
          appointment.start_time ? new Date(appointment.start_time).toDateString() : null,
        )
        .filter(Boolean),
    ).size > 1;

  // Track modal open event on mount
  useEffect(() => {
    const hasCurrentAppointment = !!eligibleCurrent && eligibleCurrent.length > 0;
    const hasUpcomingAppointment = !!eligibleNext && eligibleNext.length > 0;
    const isCurrentOvertime = !!eligibleOvertime && eligibleOvertime.length > 0;

    appointment.collisionModalOpened({
      has_current: hasCurrentAppointment,
      has_upcoming: hasUpcomingAppointment,
      has_overtime: isCurrentOvertime,
      appointment_count: appointments.length,
      confidence_level: recommendation?.confidence || 'unknown',
    });
  }, [
    eligibleCurrent,
    eligibleOvertime,
    eligibleNext,
    recommendation,
    appointments.length,
    appointment,
  ]);

  const handleUnscheduledRecording = () => {
    // Track unscheduled selection
    appointment.collisionUnscheduledSelected({
      had_appointments: appointments.length > 0,
      appointment_count: appointments.length,
      had_current: !!eligibleCurrent && eligibleCurrent.length > 0,
      had_upcoming: !!eligibleNext && eligibleNext.length > 0,
    });

    navigate('/encounters/active', { state: telehealthRouteState });
    closeModal();
  };

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      fullWidth
      data-testid="appointment-selection-modal"
      maxWidth="md"
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        data-testid="appointment-modal-title"
      >
        {isWaitingRoomTelehealthLaunch ? 'Start Video Call' : 'Start Recording Session'}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: theme => theme.palette.grey[500] }}
          data-testid="appointment-selection-modal-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 2, pb: 3 }}>
          {/* Recommendation message if available */}
          {recommendation && (
            <Typography variant="body1" sx={{ mb: 3 }}>
              {recommendation.message}
            </Typography>
          )}

          {/* Show appointments using AppointmentsList */}
          <AppointmentsList
            appointments={appointments}
            showDateHeaders={spansMultipleDays}
            defaultExpanded="none"
            analyticsSource="appointment_collision"
            navigateOptions={
              isWaitingRoomTelehealthLaunch
                ? { state: telehealthRouteState, allowNoteRedirect: false }
                : undefined
            }
            onRecordSession={() => {
              // The AppointmentCard handles navigation internally
              // Just close the modal after selection
              closeModal();
            }}
          />

          {!requiresScheduledAppointmentForNoteCreation && (
            <>
              {/* OR Divider */}
              <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                <Divider sx={{ flexGrow: 1 }} />
                <Typography variant="body2" sx={{ px: 2, color: 'text.secondary' }}>
                  OR
                </Typography>
                <Divider sx={{ flexGrow: 1 }} />
              </Box>

              {/* Option for unscheduled recording */}
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={handleUnscheduledRecording}
                data-testid="record-without-appointment-button"
              >
                {isWaitingRoomTelehealthLaunch
                  ? 'Start with a New Encounter'
                  : `Record a Different ${singularCap}`}
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentSelectionModal;
